/**
 * W3-01 (UX-015): whether the last editing session of a chart ended cleanly, kept per browser profile.
 *
 * Reopening a chart replays the journal written after its last snapshot. That is normal after a reload (the tab
 * did not compact on its way out) and only means "recovered" after a crash. The session marks the chart "open at
 * seq N" when it opens and "ended cleanly at seq M" when it closes, or when the page goes away with every change
 * acknowledged by storage. The next open of a chart whose mark still says "open" knows the previous session did not
 * end: it recovered the edits made after N (counted from the seq, so undo and redo of earlier sessions do not count).
 *
 * Kept in localStorage (synchronous: it has to be written in `pagehide`). Every access is guarded: without storage
 * (private mode, blocked site data) the marks are unknown and the session falls back to the journal replay count.
 */

import type { ProjectId } from '../core/model/types';

/** The previous session's mark of a chart. */
export interface SessionMark {
  /** The previous session ended cleanly (closed, or the page left with nothing waiting to be saved). */
  readonly clean: boolean;
  /** The document seq when that session opened the chart. */
  readonly openedSeq: number;
}

export interface SessionMarks {
  /** The previous session's mark (null: unknown, e.g. no storage or never opened with marks). */
  previous(id: ProjectId): SessionMark | null;
  /** This session opened the chart at `seq`. */
  opened(id: ProjectId, seq: number): void;
  /** This session ended cleanly. */
  ended(id: ProjectId): void;
}

const PREFIX = 'phiedit.session.';

/** Reads a stored mark (null when absent or malformed). */
export function parseMark(raw: string | null): SessionMark | null {
  if (raw === null) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (typeof v !== 'object' || v === null) return null;
    const o = v as { readonly clean?: unknown; readonly openedSeq?: unknown };
    if (typeof o.clean !== 'boolean' || typeof o.openedSeq !== 'number' || !Number.isFinite(o.openedSeq)) return null;
    return { clean: o.clean, openedSeq: o.openedSeq };
  } catch {
    return null;
  }
}

/** Marks in a Storage (localStorage by default); every access may throw and is guarded. */
export function createSessionMarks(storage: () => Storage | null = defaultStorage): SessionMarks {
  const read = (id: ProjectId): SessionMark | null => {
    try {
      return parseMark(storage()?.getItem(PREFIX + id) ?? null);
    } catch {
      return null;
    }
  };
  const write = (id: ProjectId, mark: SessionMark): void => {
    try {
      storage()?.setItem(PREFIX + id, JSON.stringify(mark));
    } catch {
      // No storage: the marks stay unknown.
    }
  };
  return {
    previous: read,
    opened(id, seq) {
      write(id, { clean: false, openedSeq: seq });
    },
    ended(id) {
      const mark = read(id);
      if (mark !== null) write(id, { ...mark, clean: true });
    },
  };
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Marks that remember nothing (tests, environments without storage). */
export const NO_SESSION_MARKS: SessionMarks = {
  previous: () => null,
  opened: () => undefined,
  ended: () => undefined,
};

/**
 * How many edits a reopened chart recovered: null when there is nothing to say (the previous session ended
 * cleanly, or it made no edits). The count is the edits since the last clean close (UX-015): the crashed session's
 * own seq range when its mark is known, else the journal replay count. It does not depend on how much the last
 * snapshot already held — a crash notice must not disappear because storage folded the journal in the meantime.
 */
export function recoveredEdits(mark: SessionMark | null, replayed: number, seq: number): number | null {
  if (mark === null) return replayed > 0 ? replayed : null;
  if (mark.clean) return null;
  const since = seq - mark.openedSeq;
  return since > 0 ? since : null;
}
